import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "../components/Shared";
import { fmtDate } from "../lib/format";
import { CalendarDays, FolderKanban } from "lucide-react";

const STATUS_STYLE = { planned: "bg-slate-100 text-slate-700 border-slate-200", in_progress: "bg-blue-100 text-blue-700 border-blue-200", done: "bg-emerald-100 text-emerald-700 border-emerald-200" };
const STATUS_LABEL = { planned: "Planned", in_progress: "In progress", done: "Done" };
const PRIORITY_BAR = { High: "border-l-red-500", Medium: "border-l-amber-500", Low: "border-l-slate-300" };
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function MySchedule() {
  const { data: items = [], isLoading } = useQuery({ queryKey: ["my-schedule-page"], queryFn: () => api.get("/work-program/mine").then((r) => r.data) });
  const today = todayStr();
  const isOverdue = (w) => w.status !== "done" && (w.end_date || w.start_date) < today;
  const upcoming = items.filter((w) => (w.end_date || w.start_date) >= today && w.status !== "done");
  const overdue = items.filter((w) => isOverdue(w));
  const done = items.filter((w) => w.status === "done");

  const counts = [
    ["Upcoming", upcoming.length, "text-blue-600"],
    ["Overdue", overdue.length, "text-red-600"],
    ["Completed", done.length, "text-emerald-600"],
  ];

  const Row = (w) => (
    <div key={w.id} data-testid={`schedule-item-${w.id}`} className={`flex items-start justify-between gap-3 px-5 py-3.5 border-l-4 ${PRIORITY_BAR[w.priority] || "border-l-slate-200"}`}>
      <div className="min-w-0">
        <div className={`text-sm font-medium ${w.status === "done" ? "text-slate-400 line-through" : "text-slate-800"}`}>{w.title}</div>
        <div className="text-xs text-slate-400 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" />{fmtDate(w.start_date)}{w.end_date && w.end_date !== w.start_date ? ` → ${fmtDate(w.end_date)}` : ""}</span>
          {w.project_name && <span className="inline-flex items-center gap-1"><FolderKanban className="h-3 w-3" />{w.project_name}</span>}
          {w.priority && <span className="text-slate-400">Priority: {w.priority}</span>}
        </div>
        {w.notes && <div className="text-xs text-slate-500 mt-1 whitespace-pre-wrap">{w.notes}</div>}
        {isOverdue(w) && <div className="text-xs font-semibold text-red-600 mt-1" data-testid={`schedule-overdue-${w.id}`}>Overdue</div>}
      </div>
      <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS_STYLE[w.status] || ""}`}>{STATUS_LABEL[w.status] || w.status}</span>
    </div>
  );

  return (
    <div data-testid="my-schedule-page">
      <PageHeader title="My Schedule" subtitle="Activities the CEO has scheduled for you" />

      <div className="grid grid-cols-3 gap-3 mb-6">
        {counts.map(([label, n, color]) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`schedule-count-${label.toLowerCase()}`}>
            <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
            <div className={`text-2xl font-bold ${color}`}>{n}</div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-sm text-slate-400">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400" data-testid="my-schedule-empty">
            No activities scheduled for you yet.
            <div className="mt-2 text-xs">When the CEO schedules an activity for you, it will appear here.</div>
          </div>
        ) : (
          <div>
            {overdue.length > 0 && (
              <div>
                <div className="px-5 py-2 bg-red-50 text-xs font-semibold uppercase tracking-wide text-red-600" data-testid="schedule-section-overdue">Overdue</div>
                <div className="divide-y divide-slate-100">{overdue.map(Row)}</div>
              </div>
            )}
            {upcoming.length > 0 && (
              <div>
                <div className="px-5 py-2 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500" data-testid="schedule-section-upcoming">Upcoming</div>
                <div className="divide-y divide-slate-100">{upcoming.map(Row)}</div>
              </div>
            )}
            {done.length > 0 && (
              <div>
                <div className="px-5 py-2 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500" data-testid="schedule-section-done">Completed</div>
                <div className="divide-y divide-slate-100">{done.map(Row)}</div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 text-xs text-slate-400">
        Looking for your tasks and projects? Visit your <Link to="/dashboard" className="text-[#1E3A8A] underline">dashboard</Link>.
      </div>
    </div>
  );
}
